import React from "react"
import {useTranslation} from "react-i18next"
import {twMerge} from "tailwind-merge"

import {
  AKlaraDashboardDealChipValues,
  AKlaraDashboardDealViewValues,
  AKlaraDashboardSummary,
  AKlaraDashboardSummaryModeEnum,
} from "../../../services/types.generated.ts"
import {KlaraFiltersContext} from "../filters.tsx"
import {isLifetime} from "../period.ts"
import {useKlaraSummary} from "../useDashboardClock.ts"
import {useKlaraPeriod} from "../useKlaraPeriod.ts"
import {CardState} from "./CardState.tsx"
import {CARD_CLASS_NAME, DELTA_PILL_CLASS_NAME, PILL_CLASS_NAME} from "./cardStyles.ts"
import {InfoDescription, InfoTooltip} from "./InfoTooltip.tsx"

export type TUpdatesDelta = {direction: "up" | "down" | "same"; difference: number} | null

/** Change of the updates collected against the previous window; none for lifetime or without a previous window. */
export const getUpdatesDelta = (current: number, previous: number | null, isLifetime: boolean): TUpdatesDelta => {
  if (isLifetime || previous === null) {
    return null
  }
  const difference = Math.abs(current - previous)
  if (difference === 0) {
    return {direction: "same", difference}
  }
  return {direction: current > previous ? "up" : "down", difference}
}

/** The five team-wide figures of the period. They ignore the sales person filter; their clicks clear it. */
export const KpiRow: React.FC = () => {
  const {t} = useTranslation()
  const [period] = useKlaraPeriod()
  const {clearForTeamDrillDown, setView, setChip, setSalesPersonExternalId, openDrawer} =
    KlaraFiltersContext.useContext()
  const {data, isPending, isError, isFetching, refetch} = useKlaraSummary()
  const summary = data?.data
  const isReach = summary?.mode === AKlaraDashboardSummaryModeEnum.Reach

  return (
    <section aria-label={t("KlaraDashboard_Kpi_Title")}>
      <CardState query={{isPending, isError, isFetching, refetch}} skeletonClassName={"h-32"}>
        {summary && (
          <ul className={"flex flex-wrap gap-4"}>
            <KpiCard
              label={t("KlaraDashboard_Kpi_Leads_Label")}
              value={summary.open_deals}
              sub={t("KlaraDashboard_Kpi_Leads_Sub")}
              info={isReach ? t("KlaraDashboard_Kpi_Leads_Info_Reach") : t("KlaraDashboard_Kpi_Leads_Info_SalesCycle")}
              onClick={() => {
                clearForTeamDrillDown()
                setView(AKlaraDashboardDealViewValues.All)
              }}
            />
            <KpiCard
              label={t("KlaraDashboard_Kpi_SalesFlows_Label")}
              value={summary.sales_flows}
              sub={t("KlaraDashboard_Kpi_SalesFlows_Sub")}
              info={
                isReach
                  ? t("KlaraDashboard_Kpi_SalesFlows_Info_Reach")
                  : t("KlaraDashboard_Kpi_SalesFlows_Info_SalesCycle")
              }
            />
            <KpiCard
              label={t("KlaraDashboard_Kpi_Missing_Label")}
              value={summary.missing}
              sub={t("KlaraDashboard_Kpi_Missing_Sub")}
              info={t("KlaraDashboard_Kpi_Missing_Info")}
              valueClassName={summary.missing > 0 ? "text-cr-yellow-deep" : undefined}
              onClick={() => {
                clearForTeamDrillDown()
                setChip(AKlaraDashboardDealChipValues.Missing)
              }}
            />
            <KpiCard
              label={t("KlaraDashboard_Kpi_UpdatesCollected_Label")}
              value={summary.updates_collected}
              sub={t("KlaraDashboard_Kpi_UpdatesCollected_Sub")}
              info={t("KlaraDashboard_Kpi_UpdatesCollected_Info")}
              valueClassName={"text-cr-blue"}
              badge={<DeltaPill summary={summary} isLifetime={isLifetime(period)} />}
              onClick={() => {
                // The Update center is team-wide, so it opens without the sales person filter.
                setSalesPersonExternalId(undefined)
                openDrawer({kind: "updates"})
              }}
            />
            <KpiCard
              label={t("KlaraDashboard_Kpi_Rotten_Label")}
              value={summary.rotten}
              sub={t("KlaraDashboard_Kpi_Rotten_Sub", {days: summary.rotten_days})}
              info={t("KlaraDashboard_Kpi_Rotten_Info", {days: summary.rotten_days})}
              onClick={() => {
                clearForTeamDrillDown()
                setChip(AKlaraDashboardDealChipValues.Rotten)
              }}
            />
          </ul>
        )}
      </CardState>
    </section>
  )
}

const KpiCard: React.FC<{
  label: string
  value: number
  sub: string
  info: string
  valueClassName?: string
  badge?: React.ReactNode
  onClick?: () => void
}> = ({label, value, sub, info, valueClassName = "text-cr-slate-90", badge, onClick}) => {
  const valueId = React.useId()
  const infoId = React.useId()
  const labelClassName = "min-w-0 text-left text-[13px] leading-snug font-semibold text-cr-grey-80 truncate"

  return (
    <li
      className={twMerge(
        CARD_CLASS_NAME,
        "relative grid min-w-0 flex-[1_1_180px] grid-cols-[minmax(0,1fr)_auto] content-start items-center gap-x-3 gap-y-1 px-5 py-[18px] lg:min-h-[142px]",
        onClick && "transition-colors hover:border-cr-blue-mid-light"
      )}
    >
      <div className={"row-start-1 flex min-w-0 items-center gap-2"}>
        {onClick ? (
          // The whole card is the button's click target; the value and sub stay its description.
          <button
            type={"button"}
            onClick={onClick}
            aria-describedby={valueId}
            className={twMerge(
              labelClassName,
              "after:absolute after:inset-0 after:rounded-xl focus-visible:outline-none focus-visible:after:outline-2 focus-visible:after:outline-cr-blue"
            )}
          >
            {label}
          </button>
        ) : (
          <span className={labelClassName}>{label}</span>
        )}
        <InfoTooltip name={label} descriptionId={infoId}>
          {info}
        </InfoTooltip>
      </div>
      <span id={valueId} className={"col-span-full row-start-2 mt-1.5 flex flex-col"}>
        <span
          className={twMerge("text-[30px] leading-[1.1] font-extrabold tracking-[-1px] tabular-nums", valueClassName)}
        >
          {value.toLocaleString()}
        </span>
        <span className={"mt-1 text-[11.5px] text-cr-slate-60"}>{sub}</span>
      </span>
      {/* After the value in reading order, beside the label on screen. */}
      {badge && <div className={"col-start-2 row-start-1 justify-self-end"}>{badge}</div>}
      <InfoDescription id={infoId}>{info}</InfoDescription>
    </li>
  )
}

const DeltaPill: React.FC<{summary: AKlaraDashboardSummary; isLifetime: boolean}> = ({summary, isLifetime}) => {
  const {t} = useTranslation()
  const delta = getUpdatesDelta(summary.updates_collected, summary.updates_collected_previous, isLifetime)

  const text = !delta
    ? t("KlaraDashboard_Kpi_Delta_None")
    : {
        up: t("KlaraDashboard_Kpi_Delta_Up", {count: delta.difference.toLocaleString()}),
        down: t("KlaraDashboard_Kpi_Delta_Down", {count: delta.difference.toLocaleString()}),
        same: t("KlaraDashboard_Kpi_Delta_Same"),
      }[delta.direction]

  return (
    <span
      data-testid={"updates delta"}
      className={twMerge(
        PILL_CLASS_NAME,
        delta?.direction === "up"
          ? DELTA_PILL_CLASS_NAME.good
          : delta?.direction === "down"
            ? DELTA_PILL_CLASS_NAME.bad
            : DELTA_PILL_CLASS_NAME.neutral
      )}
    >
      {text}
    </span>
  )
}
